import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser } from '../store/auth.actions';
import { selectAuthLoading, selectAuthError } from '../store/auth.selectors';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid2 from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import logo from './../../../assets/images/logo.png';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import {
  BoxContainer,
  AuthTextField,
  PrimaryButton,
  LogoContainer,
  StyledLogo,
  FormContainer,
} from '../../../utils/common/styles/commonStyles';

// Styled Components
const ForgotPasswordLink = styled(Link)(({ theme }) => ({
  fontSize: '0.9rem',
  fontWeight: 500,
  textDecoration: 'none',
  color: theme.palette.secondary.main,
  transition: 'all 0.3s ease',
  position: 'relative',

  '&:hover': {
    color: theme.palette.secondary.dark,
    textDecoration: 'none',
    transform: 'translateY(-1px)',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-2px',
    left: '50%',
    width: '0',
    height: '2px',
    background: theme.palette.secondary.main,
    transition: 'all 0.3s ease',
    transform: 'translateX(-50%)',
  },

  '&:hover::after': {
    width: '100%',
  },
}));

const LoginForm = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = Yup.object({
    email: Yup.string().email('Invalid Email').required('Email is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await dispatch(loginUser(values)).unwrap();
        navigate('/');
      } catch (error) {
        // Error will be handled by the error state
      }
    },
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const CustomEmailInputProps = {
    startAdornment: (
      <InputAdornment position="start">
        <Email color="action" />
      </InputAdornment>
    ),
    inputProps: {
      style: {
        paddingLeft: '12px',
      },
    },
  };

  const CustomPasswordInputProps = {
    startAdornment: (
      <InputAdornment position="start">
        <Lock color="action" />
      </InputAdornment>
    ),
    inputProps: {
      style: {
        paddingLeft: '12px',
      },
    },
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={handleTogglePasswordVisibility}
          edge="end"
          size="small"
          sx={{
            color: 'action.active',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: { xs: 2, sm: 3 },
      }}
    >
      <Grid2 container justifyContent="center">
        <Grid2 item xs={12} sm={8} md={6} lg={5}>
          <BoxContainer>
            <LogoContainer textAlign="center">
              <StyledLogo src={logo} alt="Rieker Logo" />
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{
                  color: '#2c3e50',
                  marginTop: 1,
                  background: 'linear-gradient(to right, #283048, #859398)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                SIGNS
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  marginTop: 0.5,
                  fontWeight: 500,
                }}
              >
                Welcome back! Please sign in to your account.
              </Typography>
            </LogoContainer>

            <FormContainer onSubmit={formik.handleSubmit}>
              <AuthTextField
                fullWidth
                label="Email Address"
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                margin="normal"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                slotProps={{ input: CustomEmailInputProps }}
              />

              <AuthTextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                autoComplete="current-password"
                margin="normal"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                slotProps={{ input: CustomPasswordInputProps }}
              />

              <Box mt={4} mb={3} display="flex" justifyContent="center">
                <PrimaryButton
                  type="submit"
                  variant="contained"
                  disabled={isLoading || !formik.isValid}
                  sx={{ minWidth: 140 }}
                >
                  {isLoading ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} color="inherit" />
                      <span>Signing In...</span>
                    </Box>
                  ) : (
                    'Sign In'
                  )}
                </PrimaryButton>
              </Box>

              <Fade in={Boolean(error)} timeout={300}>
                <Box mb={2}>
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        borderRadius: 2,
                        '& .MuiAlert-message': {
                          fontWeight: 500,
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  )}
                </Box>
              </Fade>

              <Box textAlign="center">
                <ForgotPasswordLink href="/forgot">Forgot your password?</ForgotPasswordLink>
              </Box>
            </FormContainer>
          </BoxContainer>
        </Grid2>
      </Grid2>
    </Container>
  );
};

export default LoginForm;
